import { useEffect, useRef, useState } from "react";
import { type SxProps, type Theme } from "@mui/material/styles";

import Icons from "assets";
import { IconButton } from "ui/icon-button";
import { InlineEditableField } from "ui/inline-editable-field";

const EDIT_BUTTON_SX: SxProps<Theme> = {
  p: 0,
  flexShrink: 0,
  "& svg": { width: 20, height: 20 },
};

type CampaignTemplateEditableTitleProps = {
  savedName: string;
  onSave: (name: string) => void;
  isSaving?: boolean;
  canEdit?: boolean;
  inputName?: string;
  inputAriaLabel?: string;
  editAriaLabel?: string;
};

export const CampaignTemplateEditableTitle = ({
  savedName,
  onSave,
  isSaving = false,
  canEdit = true,
  inputName = "campaign-template-title",
  inputAriaLabel = "Campaign brief title",
  editAriaLabel = "Edit campaign brief title",
}: CampaignTemplateEditableTitleProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftName, setDraftName] = useState(savedName);
  const previousSavedNameRef = useRef(savedName);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDraftName(savedName);
  }, [savedName]);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  useEffect(() => {
    if (!canEdit) setIsEditing(false);
  }, [canEdit]);

  useEffect(() => {
    if (previousSavedNameRef.current === savedName) {
      return;
    }

    previousSavedNameRef.current = savedName;

    if (isEditing && draftName.trim() === savedName) {
      setIsEditing(false);
    }
  }, [draftName, isEditing, savedName]);

  const trimmedDraftName = draftName.trim();
  const canSave = !!trimmedDraftName && trimmedDraftName !== savedName;

  const handleStartEdit = () => {
    setDraftName(savedName);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setDraftName(savedName);
    setIsEditing(false);
  };

  const handleSave = () => {
    if (!canSave) {
      setIsEditing(false);
      return;
    }

    onSave(trimmedDraftName);
  };

  const handleDraftNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDraftName(event.target.value);
  };

  if (isEditing) {
    return (
      <div className="w-full max-w-[512px]">
        <InlineEditableField
          ref={inputRef}
          size="small"
          name={inputName}
          aria-label={inputAriaLabel}
          value={draftName}
          onChange={handleDraftNameChange}
          onSave={handleSave}
          onCancel={handleCancel}
          canSave={canSave}
          isSaving={isSaving}
          disabled={isSaving}
        />
      </div>
    );
  }

  return (
    <div className="flex w-full max-w-[512px] items-center gap-2">
      <h1 className="text-xl font-bold leading-7 text-navy-800">{savedName}</h1>
      {!isSaving && canEdit && (
        <IconButton
          variant="text"
          size="large"
          sx={EDIT_BUTTON_SX}
          aria-label={editAriaLabel}
          onClick={handleStartEdit}
        >
          <Icons.Reply className="text-aqua-600" aria-hidden />
        </IconButton>
      )}
    </div>
  );
};

export default CampaignTemplateEditableTitle;
